import {
  Icon123,
  IconChevronDown,
  IconCoins,
  IconUserStar,
  IconBook,
} from "@tabler/icons-react";
import {
  Anchor,
  Badge,
  Burger,
  Center,
  Container,
  Group,
  Menu,
  NumberFormatter,
  Stack,
  Text,
  Tooltip,
  useComputedColorScheme,
} from "@mantine/core";
import classes from "./HeaderMenu.module.css";
import UserAvatar from "../../Boilerplate/_Shared/UserAvatar";
import { useStore } from "./store";
import { Link, useLocation } from "wouter";
import OptiviseLogoOnly from "../OptiviseLogoOnly";
import OptiviseFont from "../OptiviseFont";
import { getTenantProfile } from "./api";
import { useEffect } from "react";
import { skuBadgeTooltip, spendableSkus } from "./utils/skus";
import { isNavActive } from "./utils/navActive";
import { useMarketplaces } from "./_Shared/useMarketplaces";

const anonLinks = [{ link: "/auth/login", label: "Login" }];

const links = [
  { link: "/home", label: "Home", icon: IconUserStar },
  { link: "/imports", label: "Imports", icon: IconUserStar },
  { link: "/credits", label: "Buy SKUs", icon: IconCoins },
  // { link: "/products", label: "Products", icon: IconUserStar },

  // {
  //   link: "#1",
  //   label: "Imports",
  //   links: [
  //     { link: "/imports/foreign", label: "Foreign", icon: IconUserUp },
  //     { link: "/imports/domestic", label: "Domestic", icon: IconUserUp },
  //     { link: "/imports/asin", label: "ASIN", icon: IconUserUp },
  //   ],
  // },

  // { link: "/product-types/tree", label: "Product Types", icon: IconUserUp },
  // {
  //   link: "#1",
  //   label: "Types",
  //   links: [
  //     { link: "/product-types", label: "List", icon: IconUserUp },
  //     { link: "/product-types/tree", label: "Tree", icon: Icon123 },
  //   ],
  // },
];

const partnerLinks = [
  { link: "/partner", label: "Home", icon: IconUserStar },
  { link: "/partner/codes", label: "Codes", icon: Icon123 },
  { link: "/partner/merchants", label: "Merchants", icon: Icon123 },
  { link: "/partner/playbook", label: "Playbook", icon: IconBook },
];

const tenantAdminLinks = [{ link: "/users", label: "Users", icon: Icon123 }];

const globalAdminLinks = [
  { link: "/admin/users", label: "Users", icon: Icon123 },
  { link: "/tenants", label: "Tenants", icon: Icon123 },
  { link: "/admin/partners", label: "Partners", icon: Icon123 },
  { link: "/admin/jobs", label: "Jobs", icon: Icon123 },
  { link: "/marketplaces", label: "MPs", icon: Icon123 },
  // { link: "/esska/products", label: "Products (Esska)", icon: Icon123 },
  // { link: "/pim/schemas", label: "PIM Schemas", icon: Icon123 },
  // { link: "/admin/content", label: "Content", icon: Icon123 },
  // { link: "/translations", label: "Translations", icon: Icon123 },
];

export function HeaderMenu({ opened, toggle }) {
  const [location, setLocation] = useLocation();
  const profile = useStore((state) => state.profile);
  const tenantProfile = useStore((state) => state.tenantProfile);
  const setTenantProfile = useStore((state) => state.setTenantProfile);
  const authToken = useStore((state) => state.authToken);
  const currentTheme = useComputedColorScheme();
  const { marketplaces } = useMarketplaces();
  const skuTooltip = skuBadgeTooltip(tenantProfile, marketplaces);

  useEffect(() => {
    if (authToken) {
      getTenantProfile().then((data) => {
        setTenantProfile(data);
      });
    }
  }, [authToken, location, setTenantProfile]);

  // console.log("TENANT PROFILE", tenantProfile);
  // console.log("PROFILE", profile);

  let activeLinks = profile ? links : anonLinks;
  if (profile?.role === "MarketplacePartner") {
    activeLinks = partnerLinks;
  } else if (profile?.role === "TenantAdmin" || profile?.role === "TenantOwner") {
    activeLinks = [...links, ...tenantAdminLinks];
  }
  if (profile?.role === "GlobalAdmin") {
    activeLinks = [...links, ...globalAdminLinks];
  }
  if (!profile && authToken) {
    activeLinks = [];
  }

  const hrefs = activeLinks.map((l) => l.link);
  const items = activeLinks.map((link) => {
    const menuItems = link.links?.map((item) => (
      <Menu.Item
        component={Link}
        to={item.link}
        icon={item.icon}
        key={item.link}
      >
        {item.label}
      </Menu.Item>
    ));

    if (menuItems) {
      return (
        <Menu
          key={link.label}
          trigger="hover"
          transitionProps={{ exitDuration: 0 }}
          withinPortal
        >
          <Menu.Target>
            <Link
              to={link.link}
              className={classes.link}
              onClick={(event) => event.preventDefault()}
            >
              <Center>
                <span className={classes.linkLabel}>{link.label}</span>
                <IconChevronDown size={14} stroke={1.5} />
              </Center>
            </Link>
          </Menu.Target>
          <Menu.Dropdown>{menuItems}</Menu.Dropdown>
        </Menu>
      );
    }

    return (
      <Anchor
        component={Link}
        key={link.label}
        to={link.link}
        mod={{ active: isNavActive(location, link.link, hrefs) }}
        className={classes.link}
        // className={location.startsWith(link.link) ? classes.activeLink : classes.link}
        // onClick={(event) => event.preventDefault()}
      >
        {link.label}
      </Anchor>
    );
  });

  return (
    <header className={classes.header}>
      <Container size="xl">
        <div className={classes.inner}>
          <Group visibleFrom="xs" gap="xs">
            <OptiviseLogoOnly width={48} />
            <OptiviseFont
              color={currentTheme == "light" ? null : "#FAFAFA"}
              width={100}
            />
          </Group>

          <Group gap={5} visibleFrom="sm">
            {items}
          </Group>
          <Burger opened={opened} onClick={toggle} size="sm" hiddenFrom="sm" />
          {profile && (
            <Group
              style={{ cursor: "pointer " }}
              onClick={(event) => {
                event.preventDefault();
                setLocation("/profile");
                // toggle();
              }}
            >
              <Stack gap={4}>
                <Text visibleFrom="xs">
                  {profile.firstName ?? profile.loginName}
                </Text>
                {tenantProfile && profile?.role !== "MarketplacePartner" && (
                  <Group>
                    {tenantProfile.creditTotals &&
                      tenantProfile.creditTotals.length > 0 && (
                        <Tooltip
                          label={skuTooltip}
                          disabled={!skuTooltip}
                          multiline
                          maw={280}
                        >
                          <Badge
                            radius="sm"
                            leftSection={<IconCoins size={14} />}
                            size="xs"
                            style={{ cursor: "pointer" }}
                            onClick={(event) => {
                              event.stopPropagation();
                              setLocation("/credits");
                            }}
                          >
                            <NumberFormatter
                              thousandSeparator
                              value={spendableSkus(tenantProfile.creditTotals)}
                            />{" "}
                            SKUs
                          </Badge>
                        </Tooltip>
                      )}
                    {/* <Badge radius="sm" size="xs">
                      {tenantProfile.plan || "NONE"}
                    </Badge> */}
                  </Group>
                )}
              </Stack>
              <UserAvatar profile={profile} />
            </Group>
          )}
        </div>
      </Container>
    </header>
  );
}
